import { MetadataFieldType } from '@/api/generatedApi';
import { FilterValue } from '@/components/Search/SearchControls/SearchFiltersSection/types';
import { SearchType } from '@/components/Search/types';
import { deserializeFiltersFromURLParameters, serializeFiltersToURLParameters } from '@/components/Search/utils';
import { useFileUpload } from '@/services/fileUpload';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetName } from '@/utils/assets';
import { joinWithAnd } from '@/utils/joinWithAnd';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetAsset } from './api/assets/useGetAsset';
import { useListAllMetadataFields } from './api/metadata/useListAllMetadataFields';

const DEFAULT_MATCH_THRESHOLD = 80;

export const normalizeFilterValue = ({
  value,
  fieldName,
  metadataFields,
}: {
  value: FilterValue;
  fieldName: string;
  metadataFields: { name: string; type: MetadataFieldType }[];
}): FilterValue => {
  if (value === undefined) return value;

  const field = metadataFields.find(f => f.name === fieldName);
  if (!field) {
    return value;
  }

  switch (field.type) {
    case MetadataFieldType.Text: {
      return String(value);
    }
    case MetadataFieldType.Number: {
      if (typeof value === 'string') {
        const numberValue = Number(value);
        return Number.isNaN(numberValue) ? value : numberValue;
      }
      return value;
    }
    case MetadataFieldType.Boolean: {
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
      }
      return value;
    }
    default: {
      return value;
    }
  }
};

const isJSONArrayString = (value: string): boolean => {
  return value.trim().startsWith('[');
};

const tryParseStringArray = (value: string): string[] | undefined => {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
      return parsed;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const toggleStringInArray = (current: string[], filterValue: FilterValue): string[] => {
  const value = String(filterValue);
  return current.includes(value) ? current.filter(item => item !== value) : [...current, value];
};

const updateStringArrayFilter = (current: string[], filterValue: FilterValue): string[] => {
  if (filterValue === undefined) return [];
  if (typeof filterValue === 'string' && isJSONArrayString(filterValue)) {
    const parsed = tryParseStringArray(filterValue);
    if (parsed) return parsed;
    return toggleStringInArray(current, filterValue);
  }
  return toggleStringInArray(current, filterValue);
};

export const PARAM_NAMES = {
  QUERY: 'q',
  FOLDER_ID: 'folderId',
  ASSET_ID: 'assetId',
  SEARCH_TYPE: 'searchType',
  MIN_THRESHOLD: 'minThreshold',
  EXTENSIONS: 'extensions',
  LABELS: 'labels',
  FOLDERS: 'folders',
  METADATA: 'metadata',
  TENANT: 'tenant',
} as const;

const clearAssetSearchParameters = (parameters: URLSearchParams) => {
  parameters.delete(PARAM_NAMES.ASSET_ID);
  parameters.delete(PARAM_NAMES.SEARCH_TYPE);
  parameters.delete(PARAM_NAMES.MIN_THRESHOLD);
  parameters.delete(PARAM_NAMES.TENANT);
};

export const useSearchFilters = () => {
  const { fileUpload } = useFileUpload();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { getTenantIdFromShortName, getTenantId } = useTenants();
  const { metadataFields } = useListAllMetadataFields();

  const updateParameters = (updateFunction: (parameters: URLSearchParams) => void) => {
    const updatedParameters = new URLSearchParams(searchParameters);
    updateFunction(updatedParameters);
    setSearchParameters(updatedParameters);
  };

  const searchQuery = searchParameters.get(PARAM_NAMES.QUERY) ?? undefined;
  const folderId = searchParameters.get(PARAM_NAMES.FOLDER_ID) ?? '';
  const assetId = searchParameters.get(PARAM_NAMES.ASSET_ID) ?? '';
  const searchType = searchParameters.get(PARAM_NAMES.SEARCH_TYPE) as SearchType;

  const { asset } = useGetAsset({ assetId, enabled: !!assetId });
  const minThresholdParameter = searchParameters.get(PARAM_NAMES.MIN_THRESHOLD);
  const isInvalidThreshold =
    Number.isNaN(Number(minThresholdParameter)) ||
    Number(minThresholdParameter) < 0 ||
    Number(minThresholdParameter) > 100;
  const minThreshold =
    minThresholdParameter && !isInvalidThreshold ? Number(minThresholdParameter) : DEFAULT_MATCH_THRESHOLD;
  const selectedTenantShortName = searchParameters.get(PARAM_NAMES.TENANT) ?? '';
  const selectedTenantId = getTenantIdFromShortName(selectedTenantShortName) ?? getTenantId();

  const selectedFilters = useMemo(() => {
    return deserializeFiltersFromURLParameters(searchParameters, metadataFields);
  }, [searchParameters, metadataFields]);

  const setSelectedFilters = (newFilters: typeof selectedFilters) => {
    updateParameters(parameters => {
      parameters.delete(PARAM_NAMES.EXTENSIONS);
      parameters.delete(PARAM_NAMES.LABELS);
      parameters.delete(PARAM_NAMES.FOLDERS);
      parameters.delete(PARAM_NAMES.METADATA);

      const filterParameters = serializeFiltersToURLParameters(newFilters);
      for (const [key, value] of filterParameters.entries()) {
        parameters.set(key, value);
      }
    });
  };

  const isAssetSearch = !!assetId && ['geometric', 'part', 'visual', 'metadata', 'substitutes'].includes(searchType);
  const hasUploadedFiles = !!(fileUpload.files && fileUpload.files.length > 0);
  const isImageSearch = searchType === 'image' && hasUploadedFiles && fileUpload.useCase === 'visual-search';
  const isTextSearch = searchType === 'text' && !!searchQuery && searchQuery.trim().length > 0;
  const isEmptySearch = !isAssetSearch && !isImageSearch && !isTextSearch;

  useEffect(() => {
    if (minThresholdParameter && isInvalidThreshold) {
      updateParameters(parameters => {
        parameters.delete(PARAM_NAMES.MIN_THRESHOLD);
      });
    }
  }, [minThresholdParameter, updateParameters]);

  const handleFilterChange = (filterName: string, filterValue: string | number | boolean | undefined) => {
    const newFilters = { ...selectedFilters };

    switch (filterName) {
      case 'Folders': {
        newFilters.folders = updateStringArrayFilter(newFilters.folders, filterValue);
        break;
      }
      case 'Extensions': {
        newFilters.extensions = updateStringArrayFilter(newFilters.extensions, filterValue);
        break;
      }
      case 'Labels': {
        newFilters.labels = updateStringArrayFilter(newFilters.labels, filterValue);
        break;
      }
      default: {
        const normalizedValue = normalizeFilterValue({ value: filterValue, fieldName: filterName, metadataFields });
        if (
          normalizedValue === undefined ||
          (filterName in newFilters.metadata && newFilters.metadata[filterName] === normalizedValue)
        ) {
          delete newFilters.metadata[filterName];
        } else {
          newFilters.metadata[filterName] = normalizedValue as string | number | boolean;
        }
        break;
      }
    }

    updateParameters(parameters => {
      parameters.delete(PARAM_NAMES.EXTENSIONS);
      parameters.delete(PARAM_NAMES.LABELS);
      parameters.delete(PARAM_NAMES.FOLDERS);
      parameters.delete(PARAM_NAMES.METADATA);

      const filterParameters = serializeFiltersToURLParameters(newFilters);
      for (const [key, value] of filterParameters.entries()) {
        parameters.set(key, value);
      }
    });
  };

  const updateSearchParameters = ({
    assetId,
    searchType,
    threshold,
  }: {
    assetId?: string;
    searchType?: SearchType;
    threshold?: number;
  }) => {
    updateParameters(parameters => {
      if (assetId) {
        parameters.set(PARAM_NAMES.ASSET_ID, assetId);
      }
      if (searchType) {
        parameters.set(PARAM_NAMES.SEARCH_TYPE, searchType);
      }
      if (threshold !== undefined) {
        parameters.set(PARAM_NAMES.MIN_THRESHOLD, threshold.toString());
      }
    });
  };

  const clearAssetSearch = () => {
    updateParameters(clearAssetSearchParameters);
  };

  const updateSearchQuery = (query: string) => {
    updateParameters(parameters => {
      const trimmedQuery = query.trim();

      if (trimmedQuery) {
        parameters.set(PARAM_NAMES.QUERY, trimmedQuery);
        if (!isImageSearch && !isAssetSearch) {
          parameters.set(PARAM_NAMES.SEARCH_TYPE, 'text');
        }
      } else {
        parameters.delete(PARAM_NAMES.QUERY);
        if (searchType === 'text') {
          parameters.delete(PARAM_NAMES.SEARCH_TYPE);
        }
      }
    });
  };

  const clearAssetSearchAndSetQuery = (query: string) => {
    updateParameters(parameters => {
      clearAssetSearchParameters(parameters);

      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        parameters.set(PARAM_NAMES.QUERY, trimmedQuery);
        parameters.set(PARAM_NAMES.SEARCH_TYPE, 'text');
      } else {
        parameters.delete(PARAM_NAMES.QUERY);
      }
    });
  };

  const getSearchTitle = () => {
    const parts = ['Search results'];

    if (isAssetSearch && asset) {
      parts.push(`for ${getAssetName(asset)}`);
    } else if (isImageSearch) {
      parts.push('for images');
    }

    if (searchQuery) {
      parts.push(`containing ${searchQuery}`);
    }

    if (selectedFilters.extensions.length > 0) {
      parts.push(`containing ${joinWithAnd(selectedFilters.extensions)} files`);
    }

    const validFolders = selectedFilters.folders
      .filter(folder => folder.trim() !== '')
      .map(folder => folder.split('/').pop() || folder);

    if (validFolders.length > 0) {
      parts.push(`in ${joinWithAnd(validFolders)}`);
    }

    if (Object.keys(selectedFilters.metadata).length > 0) {
      const metadataParts = Object.entries(selectedFilters.metadata).map(([key, value]) => `${key}: ${value}`);
      parts.push(`with metadata ${joinWithAnd(metadataParts)}`);
    }

    return parts.length === 1 ? 'Search' : parts.join(' ');
  };

  return {
    assetId,
    clearAssetSearch,
    clearAssetSearchAndSetQuery,
    folderId,
    getSearchTitle,
    handleFilterChange,
    isAssetSearch,
    isEmptySearch,
    isImageSearch,
    isTextSearch,
    minThreshold,
    searchQuery,
    searchType,
    selectedFilters,
    selectedTenantShortName,
    selectedTenantId,
    setSelectedFilters,
    updateSearchParameters,
    updateSearchQuery,
  };
};
